import React, { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import { fetchFacilities } from "../services/supabaseProxy";
import { searchFacilities, getFacilitiesCount, getFacilities } from "../services/supabaseFacilitiesSearch";
import { getFacilitiesByState, cleanReservationUrl } from "../services/moabFacilitiesQuery";

// US States
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

// Component to safely render HTML content
const SafeHtmlContent = ({ html }) => {
  if (!html) return null;
  
  // Check if content looks like HTML
  const isHtml = /<[^>]*>/g.test(html);
  
  if (!isHtml) {
    return <span>{html}</span>;
  }
  
  // Sanitize the HTML to prevent XSS attacks
  const sanitizedHtml = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'br', 'p', 'span', 'div', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: ['href', 'title', 'target']
  });
  
  return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
};

const FacilityFinder = () => {
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const [totalLoaded, setTotalLoaded] = useState(0);
  const [useSupabaseSearch, setUseSupabaseSearch] = useState(false);
  const [totalDatabaseCount, setTotalDatabaseCount] = useState(0);
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Check if we should use Supabase search
  useEffect(() => {
    const checkSearchAvailability = async () => {
      try {
        const count = await getFacilitiesCount();
        if (count > 0) {
          setUseSupabaseSearch(true);
          setTotalDatabaseCount(count);
          console.log(`✓ Using Supabase search - ${count.toLocaleString()} facilities available`);
        } else {
          console.log('Supabase search not available yet - using proxy API');
        }
      } catch (err) {
        console.log('Supabase search unavailable - using proxy API');
      }
    };

    checkSearchAvailability();
  }, []);

  // Fetch facilities from Supabase or proxy
  const fetchFacilitiesData = async (query = "", pageOffset = 0) => {
    setLoading(true);
    setError(null);
    try {
      let facilitiesList = [];
      let total = 0;

      // If state is selected, fetch by state with reservation URLs
      if (selectedLocation) {
        const result = await getFacilitiesByState(selectedLocation);
        facilitiesList = result.all || [];
        total = result.total || facilitiesList.length;
      } else if (useSupabaseSearch) {
        if (query.trim().length > 0) {
          // Use full-text search if searching
          const results = await searchFacilities(query, 200);
          facilitiesList = results;
          total = facilitiesList.length;
        } else {
          // Load all facilities if no search query
          const result = await getFacilities(200, pageOffset);
          facilitiesList = result.data || [];
          // Use the pre-loaded totalDatabaseCount if available, otherwise use result.count
          total = result.count || totalDatabaseCount;
          setTotalLoaded(result.count || 0);
        }
      } else {
        // Fallback to proxy API
        const data = await fetchFacilities(200, pageOffset);
        facilitiesList = data?.RECDATA || data?.data || data?.results || [];
        total = facilitiesList.length;
      }

      setFacilities(Array.isArray(facilitiesList) ? facilitiesList : []);
      // Only update totalDatabaseCount if doing a search or location filter (specific queries)
      // Otherwise keep the database count from initial check
      if (query.trim().length > 0 || selectedLocation) {
        setTotalDatabaseCount(total);
      } else if (total > totalDatabaseCount) {
        setTotalDatabaseCount(total);
      }
      setOffset(pageOffset);
    } catch (err) {
      setError(err.message);
      setFacilities([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load - only after Supabase check is complete
  useEffect(() => {
    // Only fetch once after we know what search method to use
    // Use a small timeout to ensure totalDatabaseCount is set first
    const timer = setTimeout(() => {
      fetchFacilitiesData();
    }, 100);
    return () => clearTimeout(timer);
  }, [useSupabaseSearch]);

  // Reload when location changes
  useEffect(() => {
    if (selectedLocation) {
      fetchFacilitiesData();
    }
  }, [selectedLocation]);

  const handleSearch = (e) => {
    e.preventDefault();
    setOffset(0); // Reset to first page on search
    fetchFacilitiesData(searchQuery, 0);
  };

  // Filter facilities based on search query (only for proxy API, Supabase handles search server-side)
  const filteredFacilities = useSupabaseSearch ? facilities : facilities.filter(facility => {
    const query = (searchQuery || "").toLowerCase();
    if (!query) return true; // Show all if no search query
    
    // Search across multiple fields
    const facilityName = (facility.FacilityName || facility.facility_name || "").toLowerCase();
    const facilityId = (facility.FacilityID || facility.facility_id || "").toLowerCase();
    const description = (facility.FacilityDescription || facility.facility_description || "").toLowerCase();
    const type = (facility.FacilityTypeDescription || facility.facility_type_description || "").toLowerCase();
    const directions = (facility.FacilityDirections || facility.facility_directions || "").toLowerCase();
    
    return (
      facilityName.includes(query) ||
      facilityId.includes(query) ||
      description.includes(query) ||
      type.includes(query) ||
      directions.includes(query)
    );
  });

  const handleNextPage = () => {
    fetchFacilitiesData(searchQuery, offset + 200);
  };

  const handlePrevPage = () => {
    if (offset > 0) {
      fetchFacilitiesData(searchQuery, Math.max(0, offset - 200));
    }
  };

  return (
    <div style={{ display: "flex", height: "100%", fontFamily: "Arial, sans-serif", fontSize: "12px" }}>
      {/* Status Indicator - Click to toggle */}
      <div 
        onClick={() => setUseSupabaseSearch(!useSupabaseSearch)}
        style={{
          position: 'fixed',
          bottom: '60px',
          right: '8px',
          fontSize: '10px',
          padding: '4px 8px',
          backgroundColor: useSupabaseSearch ? '#4CAF50' : '#FF9800',
          color: 'white',
          borderRadius: '3px',
          zIndex: 1,
          cursor: 'pointer'
        }}
      >
        {useSupabaseSearch ? '📊 DB Search' : '🌐 API Mode'}
      </div>

      {/* Left Panel - Facility List */}
      <div style={{
        flex: "0 0 45%",
        borderRight: "1px solid #ccc",
        display: "flex",
        flexDirection: "column",
        background: "#f5f5f5"
      }}>
        {/* Search Bar */}
        <form onSubmit={handleSearch} style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>
          <input
            type="text"
            placeholder="Search facilities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              padding: "4px",
              border: "1px solid #999",
              fontSize: "11px",
              boxSizing: "border-box",
            }}
          />
          <button
            type="submit"
            style={{
              width: "100%",
              marginTop: "4px",
              padding: "4px",
              background: "#0066cc",
              color: "white",
              border: "none",
              cursor: "pointer",
              fontSize: "11px",
            }}
          >
            Search
          </button>
        </form>

        {/* Loading / Error */}
        {loading && <div style={{ padding: "8px", color: "#666" }}>Loading...</div>}
        {error && <div style={{ padding: "8px", color: "#c00", fontSize: "11px" }}>Error: {error}</div>}

        {/* Facility List */}
        <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
          {filteredFacilities.length === 0 && !loading && (
            <div style={{ padding: "8px", color: "#666" }}>
              {searchQuery ? `No facilities match "${searchQuery}"` : "No facilities found"}
            </div>
          )}
          {filteredFacilities.length > 0 && (
            <div style={{ fontSize: "10px", color: "#999", padding: "4px", background: "#eee" }}>
              Page: {offset > 0 ? offset + 1 : 1}-{Math.min(offset + filteredFacilities.length, filteredFacilities.length)} of {Math.max(totalDatabaseCount, facilities.length, filteredFacilities.length)}
            </div>
          )}
          {filteredFacilities.map((facility, index) => {
            const facilityId = facility.FacilityID || facility.id || index;
            const facilityName = facility.FacilityName || facility.name || "Unnamed";
            const isSelected = selectedFacility?.FacilityID === facility.FacilityID || selectedFacility?.id === facility.id;
            
            return (
              <div
                key={facilityId}
                onClick={() => setSelectedFacility(facility)}
                style={{
                  padding: "8px",
                  borderBottom: "1px solid #ddd",
                  cursor: "pointer",
                  background: isSelected ? "#e8f4f8" : "#fff",
                  fontWeight: isSelected ? "bold" : "normal",
                }}
              >
                <div style={{ fontWeight: "bold", fontSize: "11px" }}>{facilityName}</div>
                <div style={{ fontSize: "10px", color: "#666" }}>{facility.FacilityID || "ID: " + index}</div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {filteredFacilities.length > 0 && (
          <div style={{
            display: "flex",
            gap: "4px",
            padding: "8px",
            borderTop: "1px solid #ccc",
            background: "#f5f5f5"
          }}>
            <button
              onClick={handlePrevPage}
              disabled={offset === 0}
              style={{
                flex: 1,
                padding: "4px",
                background: offset === 0 ? "#ccc" : "#0066cc",
                color: "#fff",
                border: "none",
                cursor: offset === 0 ? "default" : "pointer",
                fontSize: "10px",
                opacity: offset === 0 ? 0.5 : 1,
              }}
            >
              ← Previous
            </button>
            <div style={{
              flex: 1,
              textAlign: "center",
              fontSize: "10px",
              padding: "4px",
            }}>
              Page: {offset > 0 ? offset + 1 : 1}-{Math.min(offset + filteredFacilities.length, filteredFacilities.length)} of {Math.max(totalDatabaseCount, facilities.length, filteredFacilities.length)}
            </div>
            <button
              onClick={handleNextPage}
              style={{
                flex: 1,
                padding: "4px",
                background: "#0066cc",
                color: "#fff",
                border: "none",
                cursor: "pointer",
                fontSize: "10px",
              }}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Right Panel - Facility Details */}
      <div style={{
        flex: "0 0 55%",
        padding: "12px",
        overflowY: "auto",
        background: "#fff"
      }}>
        {selectedFacility ? (
          <div>
            <h3 style={{ margin: "0 0 12px 0", fontSize: "13px" }}>
              {selectedFacility.FacilityName || "Facility"}
            </h3>

            {/* Reservation URL Section */}
            {selectedFacility.FacilityReservationURL && (
              <div style={{
                marginBottom: "12px",
                padding: "8px",
                background: "#e8f4f8",
                border: "1px solid #0066cc",
                borderRadius: "3px",
              }}>
                <div style={{ fontSize: "10px", fontWeight: "bold", marginBottom: "4px", color: "#0066cc" }}>
                  RESERVATIONS AVAILABLE
                </div>
                <div style={{ fontSize: "10px", marginBottom: "6px", color: "#666" }}>
                  Booking Platform: <strong>{(() => {
                    try {
                      const cleanedUrl = cleanReservationUrl(selectedFacility.FacilityReservationURL);
                      return new URL(cleanedUrl).hostname;
                    } catch {
                      return 'Reservation System';
                    }
                  })()}</strong>
                </div>
                <a
                  href={cleanReservationUrl(selectedFacility.FacilityReservationURL)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-block",
                    padding: "6px 12px",
                    background: "#0066cc",
                    color: "white",
                    textDecoration: "none",
                    borderRadius: "3px",
                    fontSize: "11px",
                    fontWeight: "bold",
                    cursor: "pointer"
                  }}
                >
                  ➜ View Availability & Book
                </a>
              </div>
            )}

            <div style={{ marginBottom: "12px", fontSize: "11px", lineHeight: "1.6" }}>
              <div style={{ marginBottom: "8px" }}>
                <strong>Facility ID:</strong><br />
                {selectedFacility.FacilityID}
              </div>

              {selectedFacility.FacilityDescription && (
                <div style={{ marginBottom: "8px" }}>
                  <strong>Description:</strong><br />
                  <SafeHtmlContent html={selectedFacility.FacilityDescription} />
                </div>
              )}

              {selectedFacility.FacilityDirections && (
                <div style={{ marginBottom: "8px" }}>
                  <strong>Directions:</strong><br />
                  <SafeHtmlContent html={selectedFacility.FacilityDirections} />
                </div>
              )}

              {selectedFacility.FacilityPhone && (
                <div style={{ marginBottom: "8px" }}>
                  <strong>Phone:</strong> <a href={`tel:${selectedFacility.FacilityPhone}`} style={{ color: "#0066cc" }}>{selectedFacility.FacilityPhone}</a>
                </div>
              )}

              {selectedFacility.FacilityEmail && (
                <div style={{ marginBottom: "8px" }}>
                  <strong>Email:</strong> <a href={`mailto:${selectedFacility.FacilityEmail}`} style={{ color: "#0066cc" }}>{selectedFacility.FacilityEmail}</a>
                </div>
              )}

              {selectedFacility.FacilityLatitude && selectedFacility.FacilityLongitude && (
                <div style={{ marginBottom: "8px" }}>
                  <strong>Coordinates:</strong><br />
                  Lat: {selectedFacility.FacilityLatitude}<br />
                  Lng: {selectedFacility.FacilityLongitude}
                </div>
              )}

              {selectedFacility.FacilityAdaAccess !== undefined && (
                <div style={{ marginBottom: "8px" }}>
                  <strong>ADA Access:</strong> {selectedFacility.FacilityAdaAccess ? "Yes" : "No"}
                </div>
              )}

              {selectedFacility.FacilityAccessibilityText && (
                <div style={{ marginBottom: "8px" }}>
                  <strong>Accessibility Info:</strong><br />
                  <SafeHtmlContent html={selectedFacility.FacilityAccessibilityText} />
                </div>
              )}

              {selectedFacility.Reservable !== undefined && (
                <div style={{ marginBottom: "8px" }}>
                  <strong>Reservable:</strong> {selectedFacility.Reservable ? "Yes" : "No"}
                </div>
              )}

              {selectedFacility.StayLimit && (
                <div style={{ marginBottom: "8px" }}>
                  <strong>Stay Limit:</strong> {selectedFacility.StayLimit}
                </div>
              )}

              {selectedFacility.ACTIVITY && Array.isArray(selectedFacility.ACTIVITY) && selectedFacility.ACTIVITY.length > 0 && (
                <div style={{ marginBottom: "8px" }}>
                  <strong>Activities ({selectedFacility.ACTIVITY.length}):</strong>
                  <div style={{ marginTop: "4px", paddingLeft: "8px", borderLeft: "2px solid #4CAF50" }}>
                    {selectedFacility.ACTIVITY.slice(0, 10).map((activity, i) => (
                      <div key={i} style={{ marginBottom: "2px", fontSize: "10px" }}>
                        • {activity.ActivityName || activity}
                      </div>
                    ))}
                    {selectedFacility.ACTIVITY.length > 10 && (
                      <div style={{ fontSize: "10px", color: "#999" }}>
                        ... and {selectedFacility.ACTIVITY.length - 10} more
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedFacility.CAMPSITE && Array.isArray(selectedFacility.CAMPSITE) && selectedFacility.CAMPSITE.length > 0 && (
                <div style={{ marginBottom: "8px" }}>
                  <strong>Campsites ({selectedFacility.CAMPSITE.length}):</strong>
                  <div style={{ marginTop: "4px", paddingLeft: "8px", borderLeft: "2px solid #2196F3" }}>
                    {selectedFacility.CAMPSITE.slice(0, 8).map((campsite, i) => (
                      <div key={i} style={{ marginBottom: "2px", fontSize: "10px" }}>
                        • {campsite.CampsiteName || campsite.name}
                      </div>
                    ))}
                    {selectedFacility.CAMPSITE.length > 8 && (
                      <div style={{ fontSize: "10px", color: "#999" }}>
                        ... and {selectedFacility.CAMPSITE.length - 8} more
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(selectedFacility.FacilityReservationURL || selectedFacility.FacilityMapURL) && (
                <div style={{ marginTop: "12px", display: "flex", gap: "4px" }}>
                  {selectedFacility.FacilityReservationURL && (
                    <a
                      href={selectedFacility.FacilityReservationURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        background: "#0066cc",
                        color: "white",
                        padding: "4px 8px",
                        textDecoration: "none",
                        fontSize: "11px",
                        flex: 1,
                        textAlign: "center",
                        cursor: "pointer"
                      }}
                    >
                      Reserve
                    </a>
                  )}
                  {selectedFacility.FacilityMapURL && (
                    <a
                      href={selectedFacility.FacilityMapURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        background: "#4CAF50",
                        color: "white",
                        padding: "4px 8px",
                        textDecoration: "none",
                        fontSize: "11px",
                        flex: 1,
                        textAlign: "center",
                        cursor: "pointer"
                      }}
                    >
                      Map
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ color: "#999", textAlign: "center", paddingTop: "40px" }}>
            Select a facility to view details
          </div>
        )}
      </div>
    </div>
  );
};

export default FacilityFinder;
